import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButtonGroup } from '@travelity/ui/icon-button-group';
import { IconButton } from '@travelity/ui/icon-button';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
    type GetProductsResItem2Dto,
    ResourceType,
} from '@travelity/api/generated';
import { Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useResourcePermissions } from '@contexts/user';
import DeleteIcon from '@mui/icons-material/Delete';
import { ConfirmationDialog } from '@travelity/ui/confirmation-dialog';
import { useDeleteProduct } from '@travelity/api/services';
import ExpandIcon from '@mui/icons-material/Expand';
import { OnboardingClasses } from '@/onboarding';
import { useToast } from '@/hooks';

interface PackageCardActionsProps {
    product: GetProductsResItem2Dto;
    isPackageExpanded: boolean;
    setIsPackageExpanded: (isExpanded: boolean) => void;
}

export const PackageCardActions = ({
    product,
    isPackageExpanded,
    setIsPackageExpanded,
}: PackageCardActionsProps) => {
    const { t } = useTranslation('package');
    const navigate = useNavigate();
    const { showError, showSuccess } = useToast();

    const [deletingPackageId, setDeletingPackageId] = useState('');

    const permissions = useResourcePermissions(ResourceType.PRODUCT);

    const { mutate: removeProduct } = useDeleteProduct({
        onSuccess: () => {
            setDeletingPackageId('');
            showSuccess('DELETE_PRODUCT_SUCCESS', {
                name: product.name,
            });
            navigate('/products');
        },
        onError: () => {
            showError('DELETE_PRODUCT_FAILURE', {
                name: product.name,
            });
        },
    });

    return (
        <>
            <IconButtonGroup className={OnboardingClasses.CONTROL_PANEL}>
                <IconButton
                    className={OnboardingClasses.PREVIEW_ICON}
                    icon={<VisibilityIcon fontSize="small" />}
                    href={`/products/${product.id}`}
                    tooltip={t('Preview')}
                />
                {permissions.delete && (
                    <>
                        <Divider sx={{ mx: 0.75 }} />
                        <IconButton
                            className={OnboardingClasses.DELETE_BUTTON}
                            icon={<DeleteIcon fontSize="small" />}
                            onClick={() => setDeletingPackageId(product.id)}
                            hoverColor="error.main"
                            tooltip={t('Delete')}
                        />
                    </>
                )}
                <ConfirmationDialog
                    title={t('Are you sure you want to delete this product?')}
                    content={t(
                        'This will delete this product permanently. You cannot undo this action.'
                    )}
                    confirmText={t('Delete')}
                    open={!!deletingPackageId}
                    handleCancel={() => setDeletingPackageId('')}
                    handleConfirm={() =>
                        removeProduct({ productId: deletingPackageId })
                    }
                />
            </IconButtonGroup>
            <IconButtonGroup>
                <IconButton
                    onClick={() => setIsPackageExpanded(!isPackageExpanded)}
                    icon={<ExpandIcon fontSize="small" />}
                    tooltip={isPackageExpanded ? 'Collapse' : 'Expand'}
                />
            </IconButtonGroup>
        </>
    );
};
